const DB_NAME = 'aderize-admin-dashboard';
const DB_VERSION = 1;
const STORE_NAME = 'kv';
const KEY = 'campaigns';

/** Legacy key from pre–IndexedDB cache; read once then removed. */
const LEGACY_LOCAL_KEY = 'campaigns';

let dbPromise = null;
/** After missing API or a failed open/read/write, skip persistence for this session. */
let idbDisabled = false;

function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
    });
  }
  return dbPromise;
}

function resetIdbConnection() {
  dbPromise = null;
}

function idbGet(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(KEY);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db, state) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const req = tx.objectStore(STORE_NAME).put(state, KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Read legacy `localStorage['campaigns']` if present, then remove the key.
 * Call once during app bootstrap before other reads.
 * @returns {object|null}
 */
export function consumeLegacyLocalStorageCampaigns() {
  try {
    const raw = window.localStorage.getItem(LEGACY_LOCAL_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(LEGACY_LOCAL_KEY);
    return JSON.parse(raw);
  } catch (err) {
    console.error('Legacy campaigns localStorage read failed', err);
    try {
      window.localStorage.removeItem(LEGACY_LOCAL_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
}

/**
 * @returns {Promise<object|null>}
 */
export async function getCampaignsState() {
  if (typeof indexedDB === 'undefined') {
    idbDisabled = true;
    return null;
  }
  if (idbDisabled) return null;
  try {
    const db = await openDb();
    return await idbGet(db);
  } catch {
    idbDisabled = true;
    resetIdbConnection();
    return null;
  }
}

/**
 * @param {object} state — plain object (serializable)
 * @returns {Promise<void>}
 */
export async function setCampaignsState(state) {
  if (typeof indexedDB === 'undefined') {
    idbDisabled = true;
    return;
  }
  if (idbDisabled) return;
  try {
    const db = await openDb();
    await idbPut(db, state);
  } catch {
    idbDisabled = true;
    resetIdbConnection();
  }
}
